import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  invalidateAfterImport,
  useCancelImport,
  useImportProgress,
} from "@/api/import";
import { ImportTable, isTerminal } from "@/lib/import/import-types";

/**
 * Import en cours.
 *
 * Les **echecs sont visibles pendant** la course, pas revelés a la fin : un
 * compteur d'erreurs qui monte permet d'arreter tot plutot que de decouvrir le
 * probleme sur 5 000 lignes.
 *
 * La cadence d'interrogation est dictee par le serveur (`retryAfterMs`), et
 * l'ecriture se poursuit cote serveur meme si l'utilisateur ferme la page : c'est
 * dit explicitement, et c'est vrai — l'etat vit en base, pas dans cet onglet.
 */
export const ImportStepRunning = ({
  jobId,
  table,
  onFinished,
}: {
  jobId: string;
  table: ImportTable;
  onFinished: () => void;
}) => {
  const vt = useVisibleT();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: progress } = useImportProgress(jobId);
  const { mutateAsync: cancel, isPending: cancelling } = useCancelImport();

  const status = progress?.status;

  useEffect(() => {
    if (!isTerminal(status)) return;
    // Les listes de fiches doivent refleter les creations : invalidation
    // **ciblee** sur la table importee, pas un rechargement global.
    invalidateAfterImport(queryClient, table);
    onFinished();
  }, [status, queryClient, table, onFinished]);

  const processed = progress?.processedRows ?? 0;
  const total = progress?.totalRows ?? 0;
  const terminal = isTerminal(status);

  // Phase réelle d'avancement, déduite du statut serveur et du nombre de lignes
  // écrites. `claim()` passe le job en « committing » AVANT la préparation, donc
  // « committing + 0 ligne » = préparation, pas encore d'écriture.
  //   queued      : en file d'attente
  //   preparing   : lecture du fichier / construction du plan (rien d'écrit)
  //   writing     : écriture des fiches, lot par lot (progression réelle)
  //   finalizing  : toutes les lignes traitées, clôture du job côté serveur
  //   done        : statut terminal
  type Phase = "queued" | "preparing" | "writing" | "finalizing" | "done";
  const phase: Phase = terminal
    ? "done"
    : status === "queued"
      ? "queued"
      : total > 0 && processed >= total
        ? "finalizing"
        : processed === 0
          ? "preparing"
          : "writing";

  // La barre n'atteint 100 % qu'au statut terminal : préparation et finalisation
  // gardent une réserve, pour que « 100 % » signifie vraiment « terminé » (et non
  // « écrit mais pas encore clos »). L'écriture occupe la bande 6→94 %.
  const ratio = total > 0 ? processed / total : 0;
  const displayPercent =
    phase === "done"
      ? 100
      : phase === "queued"
        ? 2
        : phase === "preparing"
          ? 6
          : phase === "finalizing"
            ? 97
            : Math.min(94, 6 + Math.round(ratio * 88));

  const phaseTitle: Record<Phase, string> = {
    queued: vt("bo.import.running.phaseQueued", "En file d'attente…"),
    preparing: vt("bo.import.running.phasePreparing", "Préparation de l'import…"),
    writing: vt("bo.import.running.title", "Écriture des fiches…"),
    finalizing: vt("bo.import.running.finalizing", "Finalisation…"),
    done: vt("bo.import.running.done", "Import terminé."),
  };
  const phaseDetail: Partial<Record<Phase, string>> = {
    queued: vt("bo.import.running.phaseQueuedHint", "Votre import va démarrer."),
    preparing: vt(
      "bo.import.running.phasePreparingHint",
      "Lecture du fichier et préparation des fiches à écrire.",
    ),
    finalizing: vt(
      "bo.import.running.finalizingHint",
      "Contrôle des lignes en erreur et clôture de l'import.",
    ),
  };
  const showRowCount = phase === "writing" || phase === "finalizing";

  return (
    <div className="flex flex-col gap-6 max-w-2xl" aria-live="polite">
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-2 font-medium">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          {phaseTitle[phase]}
        </span>
        <Progress
          value={displayPercent}
          className="h-2"
          indicatorClassName="transition-all duration-700 ease-out"
        />
        {showRowCount && total > 0 && (
          <span className="text-sm text-gray-600 tabular-nums">
            {processed.toLocaleString("fr-FR")} /{" "}
            {total.toLocaleString("fr-FR")}{" "}
            {phase === "finalizing"
              ? vt("bo.import.running.rowsProcessed", "lignes traitées")
              : vt("bo.import.running.rowsWritten", "lignes écrites")}
          </span>
        )}
        {phaseDetail[phase] && (
          <span className="text-xs text-gray-500">{phaseDetail[phase]}</span>
        )}
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          {
            label: vt("bo.import.running.created", "Créées"),
            value: progress?.createdCount ?? 0,
            tone: "text-green-700",
          },
          {
            label: vt("bo.import.running.updated", "Mises à jour"),
            value: progress?.updatedCount ?? 0,
            tone: "text-gray-900",
          },
          {
            label: vt("bo.import.running.skipped", "Ignorées"),
            value: progress?.skippedCount ?? 0,
            tone: "text-gray-500",
          },
          {
            label: vt("bo.import.running.failed", "Échecs"),
            value: progress?.errorCount ?? 0,
            tone:
              (progress?.errorCount ?? 0) > 0 ? "text-red-700" : "text-gray-500",
          },
        ].map((counter) => (
          <div key={counter.label} className="flex flex-col">
            <dt className="text-xs text-gray-500">{counter.label}</dt>
            <dd className={`text-xl font-semibold tabular-nums ${counter.tone}`}>
              {counter.value.toLocaleString("fr-FR")}
            </dd>
          </div>
        ))}
      </dl>

      <p className="text-sm text-gray-600">
        {vt(
          "bo.import.running.backgroundHint",
          "Vous pouvez fermer cette page : l'import continue sur nos serveurs.",
        )}
      </p>

      <div className="flex justify-end">
        <Button
          variant="outline"
          disabled={cancelling}
          onClick={async () => {
            try {
              await cancel(jobId);
              toast({
                title: vt(
                  "bo.import.running.cancelled",
                  "Import annulé. Les fiches déjà créées sont conservées.",
                ),
              });
            } catch {
              toast({
                variant: "destructive",
                title: vt(
                  "bo.import.running.cancelError",
                  "L'annulation a échoué.",
                ),
              });
            }
          }}
        >
          {vt("bo.import.running.cancel", "Annuler l'import")}
        </Button>
      </div>
    </div>
  );
};
